import React, { useState } from 'react';
import { AuthRole } from 'common/enums/enums';
import { useRoleContext } from 'hooks/use-role-context.hook';
import { useUserProfileValidation } from 'hooks/query';
import { useSwitchRoleMutation } from 'services/api/mutations/switch-role.mutation';
import { useNavigate } from 'react-router-dom';
import { RoleSwitchModal } from './RoleSwitchModal';

interface RoleSwitcherButtonProps {
  className?: string;
}

export const RoleSwitcherButton: React.FC<RoleSwitcherButtonProps> = ({ className = '' }) => {
  const { getSwitchButtonText, canSwitchRoles, getTargetRole } = useRoleContext();
  const { refetch: refetchUserValidation } = useUserProfileValidation();
  const switchRoleMutation = useSwitchRoleMutation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalTargetRole, setModalTargetRole] = useState<AuthRole | null>(null);

  const buttonText = getSwitchButtonText();
  const currentTargetRole = getTargetRole();

  if (!canSwitchRoles() || !buttonText || !currentTargetRole) {
    return null;
  }

  const handleButtonClick = async () => {
    if (switchRoleMutation.isPending) {
      return;
    }

    try {
      await switchRoleMutation.mutateAsync({ roleName: currentTargetRole });

      const { data: newUserValidation } = await refetchUserValidation();

      if (!newUserValidation?.isProfileValid) {
        setModalTargetRole(currentTargetRole);
        setIsModalOpen(true);
      } else {
        navigate('/');
      }
    } catch (error) {
      console.error('Failed to switch role:', error);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setModalTargetRole(null);
  };

  const isDisabled = switchRoleMutation.isPending;

  return (
    <>
      <button
        onClick={isDisabled ? undefined : handleButtonClick}
        disabled={isDisabled}
        className={`
          px-4 py-2 text-sm font-bold transition-colors duration-200 border-none bg-transparent
          ${isDisabled 
            ? 'text-gray-400 cursor-not-allowed opacity-50' 
            : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 cursor-pointer focus:outline-none'
          }
          ${className}
        `}
      >
        {buttonText}
      </button>

      {modalTargetRole && (
        <RoleSwitchModal
          isOpen={isModalOpen}
          onClose={handleCloseModal}
          targetRole={modalTargetRole}
        />
      )}
    </>
  );
};
